import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import { validateRedirectPath } from '../../utils/security';

interface LocationState {
  role?: 'brand' | 'creator';
  mode?: 'login' | 'register';
}

interface AuthenticationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (role: string) => void;
  defaultRole?: 'brand' | 'creator' | null;
  defaultMode?: 'login' | 'register';
}

const AuthenticationModal = ({
  isOpen,
  onClose,
  onSuccess,
  defaultRole = null,
  defaultMode = 'login',
}: AuthenticationModalProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const [mode, setMode] = useState<'login' | 'register'>(defaultMode);
  const [selectedRole, setSelectedRole] = useState<'brand' | 'creator' | null>(defaultRole);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    companyName: '',
  });

  useEffect(() => {
    if (locationState?.role) {
      setSelectedRole(locationState.role);
    }
    if (locationState?.mode) {
      setMode(locationState.mode);
    }
  }, [locationState]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (mode === 'register') {
      if (!formData.fullName) {
        newErrors.fullName = 'Full name is required';
      }

      if (selectedRole === 'brand' && !formData.companyName) {
        newErrors.companyName = 'Company name is required';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedRole) {
      setErrors({ role: 'Please select a role' });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));

      onSuccess?.(selectedRole);
      // Use type-safe static dashboard paths to prevent open redirect
      const dashboardPaths: Record<'brand' | 'creator', string> = {
        brand: '/brand-dashboard',
        creator: '/creator-dashboard'
      };
      const dashboardPath = dashboardPaths[selectedRole];
      navigate(dashboardPath);
      onClose();
    } catch (error) {
      setErrors({ submit: 'Authentication failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleSelect = (role: 'brand' | 'creator') => {
    setSelectedRole(role);
    setErrors((prev) => ({ ...prev, role: '' }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-1000 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-foreground/60 backdrop-blur-sm transition-smooth"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md bg-card rounded-lg shadow-glow overflow-hidden transition-slow transform">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-muted transition-quick z-10"
          aria-label="Close modal"
        >
          <Icon name="X" size={20} className="text-muted-foreground" />
        </button>

        <div className="p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg mb-4">
              <span className="text-white font-bold text-xl">N</span>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {mode === 'login' ? 'Welcome Back' : 'Get Started'}
            </h2>
            <p className="text-muted-foreground">
              {mode === 'login' ?'Sign in to access your dashboard' :'Create your account to start connecting'}
            </p>
          </div>

          {!selectedRole && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-3">
                I am a...
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleRoleSelect('brand')}
                  className="p-4 border-2 border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-quick text-center"
                >
                  <Icon name="Building2" size={24} className="mx-auto mb-2 text-primary" />
                  <span className="block font-medium text-foreground">Brand</span>
                </button>
                <button
                  onClick={() => handleRoleSelect('creator')}
                  className="p-4 border-2 border-border rounded-lg hover:border-accent hover:bg-accent/5 transition-quick text-center"
                >
                  <Icon name="Users" size={24} className="mx-auto mb-2 text-accent" />
                  <span className="block font-medium text-foreground">Creator</span>
                </button>
              </div>
              {errors.role && (
                <p className="mt-2 text-sm text-error">{errors.role}</p>
              )}
            </div>
          )}

          {selectedRole && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <>
                  <Input
                    label="Full Name"
                    type="text"
                    name="fullName"
                    placeholder="Enter your full name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    error={errors.fullName}
                    required
                  />

                  {selectedRole === 'brand' && (
                    <Input
                      label="Company Name"
                      type="text"
                      name="companyName"
                      placeholder="Enter your company name"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      error={errors.companyName}
                      required
                    />
                  )}
                </>
              )}

              <Input
                label="Email Address"
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                error={errors.email}
                required
              />

              <Input
                label="Password"
                type="password"
                name="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleInputChange}
                error={errors.password}
                required
              />

              {mode === 'register' && (
                <Input
                  label="Confirm Password"
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  error={errors.confirmPassword}
                  required
                />
              )}

              {errors.submit && (
                <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
                  <p className="text-sm text-error">{errors.submit}</p>
                </div>
              )}

              <Button
                type="submit"
                variant="default"
                fullWidth
                loading={isLoading}
                className="bg-gradient-to-r from-primary to-accent hover:opacity-90"
              >
                {mode === 'login' ? 'Sign In' : 'Create Account'}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setMode(mode === 'login' ? 'register' : 'login');
                    setErrors({});
                  }}
                  className="text-sm text-primary hover:underline transition-quick"
                >
                  {mode === 'login' ? "Don't have an account? Sign up"
                    : 'Already have an account? Sign in'}
                </button>
              </div>

              {selectedRole && (
                <button
                  type="button"
                  onClick={() => setSelectedRole(null)}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-quick"
                >
                  Change role
                </button>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthenticationModal;